import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

type ScheduledEntryCopy = {
  navLabel: string;
  scheduleThisChat: string;
  completed: string;
  failed: string;
  attention: string;
  missed: string;
  viewRun: string;
  resultThreadReadOnly: string;
  resultThreadReadOnlyDescription: string;
  unreadNotifications: string;
};

const copy = {
  en: {
    navLabel: 'Schedule',
    scheduleThisChat: 'Schedule this chat',
    completed: 'Scheduled run completed',
    failed: 'Scheduled run failed',
    attention: 'Scheduled run needs attention',
    missed: 'Scheduled one-off was missed',
    viewRun: 'View run',
    resultThreadReadOnly: 'Scheduled result · Read only',
    resultThreadReadOnlyDescription:
      'Manage or run this task from the Scheduled calendar.',
    unreadNotifications: 'unread notifications',
  },
  'pt-BR': {
    navLabel: 'Agendamentos',
    scheduleThisChat: 'Agendar esta conversa',
    completed: 'Execu\u00E7\u00E3o programada conclu\u00EDda',
    failed: 'A execu\u00E7\u00E3o programada falhou',
    attention: 'Execu\u00E7\u00E3o programada requer aten\u00E7\u00E3o',
    missed: 'A execu\u00E7\u00E3o \u00FAnica programada foi perdida',
    viewRun: 'Ver execu\u00E7\u00E3o',
    resultThreadReadOnly: 'Resultado agendado \u00B7 Somente leitura',
    resultThreadReadOnlyDescription:
      'Gerencie ou execute esta tarefa no calend\u00E1rio de agendamentos.',
    unreadNotifications: 'notifica\u00E7\u00F5es n\u00E3o lidas',
  },
  ar: {
    navLabel: 'المجدولة',
    scheduleThisChat: 'جدولة هذه المحادثة',
    completed: 'اكتمل التشغيل المجدول',
    failed: 'فشل التشغيل المجدول',
    attention: 'يحتاج التشغيل المجدول إلى مراجعة',
    missed: 'فات موعد التشغيل المجدول لمرة واحدة',
    viewRun: 'عرض التشغيل',
    resultThreadReadOnly: 'نتيجة مجدولة · للقراءة فقط',
    resultThreadReadOnlyDescription:
      'يمكنك إدارة هذه المهمة أو تشغيلها من تقويم المهام المجدولة.',
    unreadNotifications: 'إشعارات غير مقروءة',
  },
  de: {
    navLabel: 'Geplant',
    scheduleThisChat: 'Diesen Chat planen',
    completed: 'Geplanter Lauf abgeschlossen',
    failed: 'Geplanter Lauf fehlgeschlagen',
    attention: 'Geplanter Lauf muss geprüft werden',
    missed: 'Einmaliger geplanter Lauf wurde verpasst',
    viewRun: 'Lauf ansehen',
    resultThreadReadOnly: 'Geplantes Ergebnis · Schreibgeschützt',
    resultThreadReadOnlyDescription:
      'Verwalte oder starte diese Aufgabe im Kalender „Geplant“.',
    unreadNotifications: 'ungelesene Benachrichtigungen',
  },
  es: {
    navLabel: 'Programado',
    scheduleThisChat: 'Programar este chat',
    completed: 'Ejecución programada completada',
    failed: 'La ejecución programada ha fallado',
    attention: 'La ejecución programada requiere atención',
    missed: 'Se perdió la ejecución única programada',
    viewRun: 'Ver ejecución',
    resultThreadReadOnly: 'Resultado programado · Solo lectura',
    resultThreadReadOnlyDescription:
      'Gestiona o ejecuta esta tarea desde el calendario Programado.',
    unreadNotifications: 'notificaciones sin leer',
  },
  fr: {
    navLabel: 'Planifié',
    scheduleThisChat: 'Planifier cette discussion',
    completed: 'Exécution planifiée terminée',
    failed: 'Échec de l’exécution planifiée',
    attention: 'L’exécution planifiée demande votre attention',
    missed: 'L’exécution unique planifiée a été manquée',
    viewRun: 'Voir l’exécution',
    resultThreadReadOnly: 'Résultat planifié · Lecture seule',
    resultThreadReadOnlyDescription:
      'Gérez ou lancez cette tâche depuis le calendrier Planifié.',
    unreadNotifications: 'notifications non lues',
  },
  hi: {
    navLabel: 'शेड्यूल्ड',
    scheduleThisChat: 'इस चैट को शेड्यूल करें',
    completed: 'शेड्यूल किया गया रन पूरा हुआ',
    failed: 'शेड्यूल किया गया रन विफल हुआ',
    attention: 'शेड्यूल किए गए रन पर ध्यान देना आवश्यक है',
    missed: 'एक बार वाला शेड्यूल रन छूट गया',
    viewRun: 'रन देखें',
    resultThreadReadOnly: 'शेड्यूल किया गया परिणाम · केवल पढ़ने के लिए',
    resultThreadReadOnlyDescription:
      'इस कार्य को शेड्यूल्ड कैलेंडर से प्रबंधित करें या चलाएँ।',
    unreadNotifications: 'अपठित सूचनाएँ',
  },
  id: {
    navLabel: 'Terjadwal',
    scheduleThisChat: 'Jadwalkan chat ini',
    completed: 'Eksekusi terjadwal selesai',
    failed: 'Eksekusi terjadwal gagal',
    attention: 'Eksekusi terjadwal perlu perhatian',
    missed: 'Eksekusi terjadwal satu kali terlewat',
    viewRun: 'Lihat eksekusi',
    resultThreadReadOnly: 'Hasil terjadwal · Hanya baca',
    resultThreadReadOnlyDescription:
      'Kelola atau jalankan tugas ini dari kalender Terjadwal.',
    unreadNotifications: 'notifikasi belum dibaca',
  },
  it: {
    navLabel: 'Programmati',
    scheduleThisChat: 'Pianifica questa chat',
    completed: 'Esecuzione pianificata completata',
    failed: 'Esecuzione pianificata non riuscita',
    attention: 'L’esecuzione pianificata richiede attenzione',
    missed: 'L’esecuzione singola pianificata è stata persa',
    viewRun: 'Vedi esecuzione',
    resultThreadReadOnly: 'Risultato pianificato · Sola lettura',
    resultThreadReadOnlyDescription:
      'Gestisci o avvia questa attività dal calendario Programmati.',
    unreadNotifications: 'notifiche non lette',
  },
  ja: {
    navLabel: 'スケジュール',
    scheduleThisChat: 'このチャットをスケジュール',
    completed: 'スケジュール実行が完了しました',
    failed: 'スケジュール実行に失敗しました',
    attention: 'スケジュール実行の確認が必要です',
    missed: '1 回のみのスケジュール実行を逃しました',
    viewRun: '実行を表示',
    resultThreadReadOnly: 'スケジュール結果 · 読み取り専用',
    resultThreadReadOnlyDescription:
      'このタスクの管理や実行はスケジュールカレンダーから行えます。',
    unreadNotifications: '未読の通知',
  },
  ko: {
    navLabel: '예약',
    scheduleThisChat: '이 채팅 예약',
    completed: '예약 실행이 완료되었습니다',
    failed: '예약 실행에 실패했습니다',
    attention: '예약 실행을 확인해야 합니다',
    missed: '한 번만 실행되는 예약을 놓쳤습니다',
    viewRun: '실행 보기',
    resultThreadReadOnly: '예약 결과 · 읽기 전용',
    resultThreadReadOnlyDescription:
      '예약 캘린더에서 이 작업을 관리하거나 실행하세요.',
    unreadNotifications: '읽지 않은 알림',
  },
  ru: {
    navLabel: 'По расписанию',
    scheduleThisChat: 'Запланировать этот чат',
    completed: 'Запланированный запуск завершён',
    failed: 'Запланированный запуск завершился ошибкой',
    attention: 'Запланированный запуск требует внимания',
    missed: 'Однократный запуск по расписанию пропущен',
    viewRun: 'Посмотреть запуск',
    resultThreadReadOnly: 'Результат по расписанию · Только чтение',
    resultThreadReadOnlyDescription:
      'Управляйте этой задачей и запускайте её в календаре расписаний.',
    unreadNotifications: 'непрочитанные уведомления',
  },
  vi: {
    navLabel: 'Đã lên lịch',
    scheduleThisChat: 'Lên lịch cuộc trò chuyện này',
    completed: 'Lần chạy theo lịch đã hoàn tất',
    failed: 'Lần chạy theo lịch đã thất bại',
    attention: 'Lần chạy theo lịch cần chú ý',
    missed: 'Đã lỡ lần chạy một lần theo lịch',
    viewRun: 'Xem lần chạy',
    resultThreadReadOnly: 'Kết quả theo lịch · Chỉ đọc',
    resultThreadReadOnlyDescription:
      'Quản lý hoặc chạy tác vụ này từ lịch Đã lên lịch.',
    unreadNotifications: 'thông báo chưa đọc',
  },
  zh: {
    navLabel: '计划任务',
    scheduleThisChat: '为此聊天创建计划',
    completed: '计划运行已完成',
    failed: '计划运行失败',
    attention: '计划运行需要处理',
    missed: '错过了一次性计划运行',
    viewRun: '查看运行',
    resultThreadReadOnly: '计划结果 · 只读',
    resultThreadReadOnlyDescription: '请从计划任务日历管理或运行此任务。',
    unreadNotifications: '未读通知',
  },
  'zh-TW': {
    navLabel: '排程任務',
    scheduleThisChat: '為此對話建立排程',
    completed: '排程執行已完成',
    failed: '排程執行失敗',
    attention: '排程執行需要處理',
    missed: '錯過了一次性排程執行',
    viewRun: '查看執行',
    resultThreadReadOnly: '排程結果 · 唯讀',
    resultThreadReadOnlyDescription: '請從排程任務日曆管理或執行此任務。',
    unreadNotifications: '未讀通知',
  },
} as const satisfies Record<SupportedLocale, ScheduledEntryCopy>;

export function getScheduledEntryCopy(locale: string): ScheduledEntryCopy {
  return copy[normalizeLocale(locale)];
}
